import React, { useState, useEffect } from "react";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import apiService from "../../services/ApiService";
import type { VerificatorMember } from "../../types/api";

interface DateRange {
  startDate: string;
  endDate: string;
}

interface BitboxReportData {
  block_updated_at: string;
  block_status: string;
  cam1_barcode: string;
  cam1_manual_override: boolean;
  cam1_timestamp: string;
  cam2_barcode: string;
  cam2_manual_override: boolean;
  cam2_timestamp: string;
  cam3_barcode: string;
  cam3_manual_override: boolean;
  cam3_timestamp: string;
  cam4_barcode: string;
  cam4_manual_override: boolean;
  cam4_timestamp: string;
  person_in_charge: string;
}

type ReportsPanelProps = object;

const STORAGE_KEY = "bitbox_report_last_selected_users";

export const ReportsPanel: React.FC<ReportsPanelProps> = () => {
  const [showBitboxModal, setShowBitboxModal] = useState(false);
  const [bitboxDateRange, setBitboxDateRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<VerificatorMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // Only fetch verificator users once on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await apiService.manageMember({
          action: "list",
          params: { role: "verificator" },
        });
        setAvailableUsers((response as VerificatorMember[]) || []);

        // Load last selected users from localStorage
        const lastSelectedUsers = localStorage.getItem(STORAGE_KEY);
        if (lastSelectedUsers) {
          try {
            const parsedUsers = JSON.parse(lastSelectedUsers) as string[];
            // Only keep users that still exist in the available users
            const validUsers = parsedUsers.filter((userId) =>
              (response as VerificatorMember[]).some(
                (user) => user.id === userId
              )
            );
            setSelectedUsers(validUsers);
          } catch (error) {
            console.error("Failed to parse stored users:", error);
          }
        }
      } catch (error) {
        console.error("Failed to load verificator users:", error);
        setError("Failed to load verificator users. Please try again.");
        setAvailableUsers([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Save selected users to localStorage whenever they change
  useEffect(() => {
    if (selectedUsers.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedUsers));
    }
  }, [selectedUsers]);

  const handleGenerateReportClick = () => {
    setShowBitboxModal(true);
    setError("");
    // Don't reset selectedUsers - they should persist from last time
  };

  const generateCSV = (data: BitboxReportData[]): string => {
    const headers = [
      "Block Updated At",
      "Block Status",
      "Camera 1 Barcode",
      "Camera 1 Recording Mode",
      "Camera 1 Timestamp",
      "Camera 2 Barcode",
      "Camera 2 Recording Mode",
      "Camera 2 Timestamp",
      "Camera 3 Barcode",
      "Camera 3 Recording Mode",
      "Camera 3 Timestamp",
      "Camera 4 Barcode",
      "Camera 4 Recording Mode",
      "Camera 4 Timestamp",
      "Person in Charge",
    ];

    // Helper function to format timestamp to local timezone
    const formatTimestamp = (timestamp: string): string => {
      if (!timestamp || timestamp === null || timestamp === undefined)
        return "";
      try {
        // Handle format like "2025-07-09 03:16:09" which is in UTC+7
        if (timestamp.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
          // Parse the timestamp and treat it as UTC+7
          const [datePart, timePart] = timestamp.split(" ");
          const [year, month, day] = datePart.split("-").map(Number);
          const [hour, minute, second] = timePart.split(":").map(Number);

          // Create date in UTC+7 by creating a date and adjusting for the timezone offset
          const utc7Date = new Date(year, month - 1, day, hour, minute, second);

          // Convert from UTC+7 to UTC by subtracting 7 hours
          const utcDate = new Date(utc7Date.getTime() - 7 * 60 * 60 * 1000);

          // Return in local timezone
          return utcDate.toLocaleString();
        }

        // Fallback for other timestamp formats
        const date = new Date(timestamp);
        return date.toLocaleString();
      } catch (error) {
        console.log("Failed to parse timestamp:", timestamp, error);
        return timestamp; // Return original if parsing fails
      }
    };

    // Helper function to escape CSV values
    const escapeCSVValue = (value: unknown): string => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      // If value contains comma, quote, or newline, wrap in quotes and escape quotes
      if (
        stringValue.includes(",") ||
        stringValue.includes('"') ||
        stringValue.includes("\n")
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvRows = [
      headers.join(","),
      ...data.map((row) =>
        [
          escapeCSVValue(formatTimestamp(row.block_updated_at)),
          escapeCSVValue(row.block_status),
          escapeCSVValue(row.cam1_barcode),
          escapeCSVValue(row.cam1_manual_override ? 'Manual' : 'Auto'),
          escapeCSVValue(formatTimestamp(row.cam1_timestamp)),
          escapeCSVValue(row.cam2_barcode),
          escapeCSVValue(row.cam1_manual_override ? 'Manual' : 'Auto'),
          escapeCSVValue(formatTimestamp(row.cam2_timestamp)),
          escapeCSVValue(row.cam3_barcode),
          escapeCSVValue(row.cam1_manual_override ? 'Manual' : 'Auto'),
          escapeCSVValue(formatTimestamp(row.cam3_timestamp)),
          escapeCSVValue(row.cam4_barcode),
          escapeCSVValue(row.cam1_manual_override ? 'Manual' : 'Auto'),
          escapeCSVValue(formatTimestamp(row.cam4_timestamp)),
          escapeCSVValue(row.person_in_charge),
        ].join(",")
      ),
    ];

    return csvRows.join("\n");
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBitboxReportGenerate = async () => {
    if (
      !bitboxDateRange.startDate ||
      !bitboxDateRange.endDate ||
      selectedUsers.length === 0
    ) {
      setError("Please select date range and at least one verificator user.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      // Save current selection to localStorage before generating report
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedUsers));

      // Create start date at beginning of day (00:00:00) in local timezone
      const [startYear, startMonth, startDay] = bitboxDateRange.startDate
        .split("-")
        .map(Number);
      const startDate = new Date(
        startYear,
        startMonth - 1,
        startDay,
        0,
        0,
        0,
        0
      );

      // Create end date at end of day (23:59:59) in local timezone
      const [endYear, endMonth, endDay] = bitboxDateRange.endDate
        .split("-")
        .map(Number);
      const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

      const response = await apiService.generateBitboxReport({
        from_date: startDate.toISOString(),
        to_date: endDate.toISOString(),
        verificator_user_ids: selectedUsers,
      });

      if (response.data && response.data.length > 0) {
        const csvContent = generateCSV(response.data);
        const filename = `bitbox-report-${bitboxDateRange.startDate}-to-${bitboxDateRange.endDate}.csv`;
        downloadCSV(csvContent, filename);
      } else {
        setError("No data found for the selected criteria.");
        return;
      }

      // Close modal and reset date range only (keep selected users)
      setShowBitboxModal(false);
      setBitboxDateRange({ startDate: "", endDate: "" });
      // Don't reset selectedUsers - they'll be remembered for next time
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Failed to generate bitbox report:", error);
      if (error.message) {
        setError(error.message);
      } else {
        setError("Failed to generate bitbox report. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === availableUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(availableUsers.map((user) => user.id));
    }
  };

  console.log(availableUsers, "Available Users");

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4 text-gray-800">
        Generate Bitbox Report
      </h2>
      <div className="flex flex-col gap-4 mb-6">
        <p className="text-gray-600">
          Generate a detailed report of BitboxBlock data with associated barcode
          scan history.
        </p>
        {selectedUsers.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Last selected verificators:</span>{" "}
              {selectedUsers.length} user(s) will be pre-selected
            </p>
          </div>
        )}
        <Button
          variant="primary"
          onClick={handleGenerateReportClick}
          className="self-start"
        >
          Generate Report
        </Button>
      </div>

      {/* Bitbox Report Modal */}
      <Modal
        isOpen={showBitboxModal}
        onClose={() => {
          setShowBitboxModal(false);
          setError("");
        }}
        title="Generate Bitbox Report"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowBitboxModal(false);
                setError("");
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBitboxReportGenerate}
              disabled={
                !bitboxDateRange.startDate ||
                !bitboxDateRange.endDate ||
                selectedUsers.length === 0 ||
                isLoading
              }
            >
              {isLoading ? "Generating..." : "Generate Report"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date:
            </label>
            <input
              type="date"
              value={bitboxDateRange.startDate}
              onChange={(e) =>
                setBitboxDateRange((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }))
              }
              className="w-full p-2 border border-gray-300 rounded"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date:
            </label>
            <input
              type="date"
              value={bitboxDateRange.endDate}
              min={bitboxDateRange.startDate}
              onChange={(e) =>
                setBitboxDateRange((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
              className="w-full p-2 border border-gray-300 rounded"
              disabled={isLoading}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Verificator Users:
              </label>
              <button
                type="button"
                onClick={handleSelectAllUsers}
                className="text-sm text-blue-600 hover:text-blue-800"
                disabled={isLoading || availableUsers.length === 0}
              >
                {selectedUsers.length === availableUsers.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>

            {isLoading ? (
              <div className="text-center py-4 text-gray-500">
                Loading users...
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No users available
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded p-2">
                {availableUsers.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center space-x-2 p-1 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                      className="rounded"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      {user.full_name} ({user.username})
                    </span>
                  </label>
                ))}
              </div>
            )}

            {selectedUsers.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedUsers.length} user(s) selected
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
